/**
 * Mi Boleta — lógica del buscador principal (`SearchBar`).
 *
 * Pieza **pura y sin React**, como el resto de las decisiones de esta carpeta
 * (`flashSaleCta`, `signupForm`, `loginMode`): qué filtros hay, cuándo una
 * búsqueda "tiene algo" y cómo se traduce a parámetros de URL se verifica **sin
 * montar interfaz**. El componente `.tsx` queda fino: estado local + presentación.
 *
 * ⚠️ **Los valores todavía no consultan al backend.** El requerimiento pide
 * dejar el estado "listo para conectarse después". `buildSearchParams` produce
 * exactamente lo que una futura llamada a `/eventos?...` necesitará, de modo que
 * el día que se conecte no haya que reescribir la forma de los datos.
 */

/** Una opción de un filtro desplegable. `value` vacío = "sin filtrar". */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Estado completo del buscador. Cadenas vacías = "cualquiera": así un filtro sin
 * elegir no viaja en la URL, en vez de mandar un `city=` que el backend tendría
 * que aprender a ignorar.
 */
export interface SearchFilters {
  city: string;
  category: string;
  /** Clave de un preset (`hoy`, `finde`…) o `YYYY-MM-DD`. */
  date: string;
  /** Texto libre: artista, evento… */
  query: string;
}

export type SearchField = keyof SearchFilters;

/** Placeholder EXACTO pedido en el diseño. */
export const SEARCH_PLACEHOLDER = 'Busca aquí tu evento';

/**
 * Opciones provisionales. Se dejan acá —y no en el componente— para que sean
 * un solo punto a cambiar cuando lleguen del backend, y para poder verificarlas.
 * La primera de cada lista es el estado "sin elegir".
 */
export const CITY_OPTIONS: readonly FilterOption[] = [
  { value: '', label: 'Ciudad' },
  { value: 'bogota', label: 'Bogotá' },
  { value: 'medellin', label: 'Medellín' },
  { value: 'cali', label: 'Cali' },
  { value: 'barranquilla', label: 'Barranquilla' },
  { value: 'cartagena', label: 'Cartagena' },
];

export const CATEGORY_OPTIONS: readonly FilterOption[] = [
  { value: '', label: 'Categoría' },
  { value: 'conciertos', label: 'Conciertos' },
  { value: 'teatro', label: 'Teatro' },
  { value: 'deportes', label: 'Deportes' },
  { value: 'fiestas', label: 'Fiestas' },
  { value: 'experiencias', label: 'Experiencias' },
];

export const DATE_OPTIONS: readonly FilterOption[] = [
  { value: '', label: 'Fecha' },
  { value: 'hoy', label: 'Hoy' },
  { value: 'manana', label: 'Mañana' },
  { value: 'finde', label: 'Este fin de semana' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
];

/** Estado inicial: todo vacío. */
export function emptySearch(): SearchFilters {
  return { city: '', category: '', date: '', query: '' };
}

/**
 * ¿La búsqueda tiene algo que buscar?
 *
 * Importa para no disparar una navegación vacía: apretar la lupa sin filtros ni
 * texto no debería llevar a `/eventos?` a secas. El texto se considera sólo si
 * tiene contenido real (espacios no cuentan).
 */
export function isSearchActive(filters: SearchFilters): boolean {
  return Boolean(filters.city || filters.category || filters.date || filters.query.trim());
}

/**
 * Traduce los filtros a `URLSearchParams`, listos para `/eventos?...`.
 *
 * - Sólo se incluyen los campos con valor: un filtro sin elegir **no** viaja.
 * - El texto se recorta: un `query=%20%20` ensuciaría la URL y la búsqueda.
 * - El orden es estable (ciudad, categoría, fecha, q) para que dos búsquedas
 *   iguales produzcan la MISMA URL — útil para caché y para compartir enlaces.
 */
export function buildSearchParams(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.city) params.set('city', filters.city);
  if (filters.category) params.set('category', filters.category);
  if (filters.date) params.set('date', filters.date);
  const q = filters.query.trim();
  if (q) params.set('q', q);
  return params;
}

/**
 * Destino de la búsqueda: `/eventos` con los parámetros, o `/eventos` a secas si
 * no hay nada. Es lo que el componente pasa a `navigate`.
 */
export function searchTarget(filters: SearchFilters): string {
  const qs = buildSearchParams(filters).toString();
  return qs ? `/eventos?${qs}` : '/eventos';
}
